import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import PitchWatcher from "~/components/agencies/PitchWatcher";
import AgencySidebar from "~/components/AgencySidebar";
import NavigationHeader from "~/components/NavigationHeader";
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar";
import { Toaster } from "~/components/ui/sonner";
import { useAuth } from "~/context/AuthContext";
import { BreadcrumbProvider } from "~/context/BreadcrumbContext";
import { useScrollRestoration } from "~/hooks/useScrollRestoration";
import { useLocation, useNavigate } from "~/lib/team-router";

export default function AgencyLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useScrollRestoration<HTMLDivElement>("agency");
  const [sidebarOpen] = useState(() => {
    const saved = localStorage.getItem("sidebar:state");
    return saved !== null ? saved === "true" : true;
  });

  const canManage =
    user?.agency?.role === "owner" || user?.agency?.role === "admin";

  useEffect(() => {
    if (user && !user.agency) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.agency || canManage) return;
    const privileged =
      location.pathname.startsWith("/agency/people") ||
      location.pathname.startsWith("/agency/clients") ||
      location.pathname.startsWith("/agency/settings");
    if (privileged) {
      navigate("/agency/home");
    }
  }, [user, canManage, location.pathname, navigate]);

  useEffect(() => {
    localStorage.setItem("sidebar:state", sidebarOpen.toString());
  }, [sidebarOpen]);

  if (!user?.agency) {
    return null;
  }

  return (
    <BreadcrumbProvider>
      <Toaster richColors position="top-center" />
      <SidebarProvider
        className="h-screen min-h-0 overflow-hidden"
        style={
          {
            "--sidebar-width": "calc(var(--spacing) * 72)",
            "--header-height": "calc(var(--spacing) * 12)",
          } as React.CSSProperties
        }
      >
        <AgencySidebar sidebarOpen={sidebarOpen} />
        <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <NavigationHeader conversations={[]} />
          <PitchWatcher />
          <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div
              ref={scrollContainerRef}
              className="@container/main flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto"
            >
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </BreadcrumbProvider>
  );
}
